import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import type * as graphql from '@treams/treams-network';
import { Skeleton, Tooltip, space, tokens } from '@treams/treams-ui';

export type TrafficLightMetric =
	| graphql.AnalyticsIntegerMetric
	| graphql.AnalyticsFloatMetric
	| graphql.AnalyticsPercentageMetric
	| graphql.AnalyticsFractionMetric
	| (graphql.AnalyticsConversationTemplateMetric & { id: string; threshold: number });

function formatFloat(value: number) {
	return (Math.round(value * 10) / 10) % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
}

function formatThresholdMetric(metric: TrafficLightMetric) {
	const value =
		metric.__typename === 'AnalyticsFractionMetric' ||
		metric.__typename === 'AnalyticsConversationTemplateMetric'
			? Number((metric.threshold * metric.total).toFixed(0))
			: metric.threshold;

	switch (metric.__typename) {
		case 'AnalyticsPercentageMetric':
			return value ? `${formatFloat(value * 100)}%` : '—';
		case 'AnalyticsIntegerMetric':
			return value;
		case 'AnalyticsFloatMetric':
			return value ? formatFloat(value) : '—';
		case 'AnalyticsFractionMetric':
		case 'AnalyticsConversationTemplateMetric':
			return `${value} / ${metric.total}`;
	}
}

export function ThresholdTitle(props: {
	metric: graphql.AnalyticsMetric | TrafficLightMetric;
	signal: 'good' | 'bad';
}) {
	const { t } = useTranslation();

	const translationKey = (() => {
		if (props.metric.id === 'engagement.average') {
			return 'engagement.score';
		}

		if (props.metric.id === 'competencies.total') {
			return 'totalCompetenciesMeasured';
		}

		if (
			props.metric.__typename === 'AnalyticsIntegerMetric' ||
			props.metric.__typename === 'AnalyticsFloatMetric'
		) {
			return 'averagePerEmployee';
		}

		if (props.metric.__typename === 'AnalyticsPercentageMetric') {
			return 'percentage';
		}

		if (
			props.metric.__typename === 'AnalyticsFractionMetric' ||
			props.metric.__typename === 'AnalyticsConversationTemplateMetric'
		) {
			return 'fraction';
		}

		return 'Missing key';
	})();

	return (
		<>
			{t(`analytics.metric.kpi.expectation.${props.signal}.${translationKey}`, {
				value: formatThresholdMetric(props.metric as TrafficLightMetric),
			})}
		</>
	);
}

const TrafficLightElement = styled(Box)<{ signal: 'bad' | 'neutral' | 'good' }>`
	width: ${space.xs}px;
	height: ${space.xs}px;
	border-radius: ${tokens.radius.full}px;
	background-color: ${(props) =>
		props.signal === 'bad'
			? tokens.error.icon
			: props.signal === 'good'
				? tokens.success.icon
				: tokens.icon.tertiary};
	flex-shrink: 0;
`;

export function TrafficLight(props: {
	value?: number;
	totalUsers?: number;
	threshold: number;
	metric?: graphql.AnalyticsMetric | TrafficLightMetric;
}) {
	const signal: 'good' | 'bad' =
		props.value && props.value >= (props.totalUsers ?? 1) * props.threshold ? 'good' : 'bad';

	const dot = <TrafficLightElement ml={2} signal={signal} />;

	if (!props.metric) {
		return dot;
	}

	return (
		<Tooltip
			dark
			fitContent
			portal
			content={<ThresholdTitle metric={props.metric} signal={signal} />}
		>
			{dot}
		</Tooltip>
	);
}

TrafficLight.Element = TrafficLightElement;

TrafficLight.Skeleton = styled(Skeleton.Box)`
	width: ${space.xs}px;
	height: ${space.xs}px;
	border-radius: ${tokens.radius.full}px;
`;
